import { NavLink } from 'react-router-dom';
import { Lock, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { ISideBarItem, ISideBarSubItem } from '../../../resources/interfaces';
import useUserAccount from '../../../hooks/userAccountHook';
import useRoute from '../../../hooks/routesHook';
import useModal from '../../../hooks/modalHook';
import Tooltip from '../../ToolTip';

interface NavigationItemProps {
  nav: ISideBarItem;
  index: number;
  isMobile?: boolean;
  isSidebarExpanded: boolean;
  onMenuClick: () => void;
  expandedNavItems: Set<string>;
  toggleNavItem: (path: string) => void;
  handleToggleFullNav?: () => void;
}

export default function NavigationItem({ 
  nav, 
  index, 
  isMobile = false, 
  isSidebarExpanded, 
  onMenuClick, 
  expandedNavItems, 
  toggleNavItem,
  handleToggleFullNav
}: NavigationItemProps) {
  const { user } = useUserAccount();
  const { getDashboardBase } = useRoute();
  const { ModalComponent,closeModal, updateModalStates } = useModal();
  const fullPath = `${window.location?.pathname}${window.location?.search}`;

  const isActiveSubNav = (navPath: string): boolean => {
    const cleanNavPath = navPath.replace(/^\//, '');
    const adminPrefixedPath = `${getDashboardBase()}/${cleanNavPath}`;
    return fullPath.includes(adminPrefixedPath) || fullPath.includes(navPath);
  };

  const activeSubNav = (id: number) => {
    const isMotorPage = fullPath.includes('type=motor');
    const isNonMotorPage = fullPath.includes('type=non-motor');
    const isClaimsPage = fullPath.includes('claims');
    const isIssuedMotorPolicyPage = (fullPath === `${getDashboardBase()}/policies/auto-policy/add-issued-motor-policy` && id === 0);
    return (
      (isIssuedMotorPolicyPage ||
        (isMotorPage && !isClaimsPage && id === 0) ||
        (isNonMotorPage && !isClaimsPage && id === 1) ||
        (isMotorPage && isClaimsPage && id === 2) ||
        (isNonMotorPage && isClaimsPage && id === 3)
      )
    );
  };

  const isActive = isActiveSubNav(nav.path);
  const hasActiveChild = nav.children?.some(child => isActiveSubNav(child.path));
  const isExpanded = expandedNavItems.has(nav.path) || isActive || hasActiveChild;
  
  const baseClasses = 'flex items-center w-full text-sm rounded-md transition-colors duration-200';
  const activeClasses = 'bg-brolly-yellow/40 font-medium';
  const hoverClasses = 'hover:bg-primary-surface dark:hover:bg-darkBg-border';
  const textClasses = 'text-gray-700 dark:text-white';
  const lockedClasses = nav.locked ? 'opacity-60 cursor-not-allowed' : '';

  const handleClick = () => {
    if (nav.locked) {
      updateModalStates({
        show: true,
        title: 'Feature Locked',
        size: 'sm',
        showCloseButton: true,
        static: false,
        children: (
          <div className="text-center p-4">
            <Lock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText mb-2">
              Claims and More Tools
            </h3>
            <p className="text-sm text-gray-600 dark:text-darkBg-subText">
              These features are currently locked for your account. Contact your administrator to get access.
            </p>
            <button
              onClick={closeModal}
              className="mt-4 px-4 py-2 bg-primary-main text-white rounded-md hover:bg-opacity-90"
            >
              Got it
            </button>
          </div>
        )
      });
      return;
    }

    if (nav.hasChildren && isSidebarExpanded) {
      toggleNavItem(nav.path);
    }
  };

  // For collapsed sidebar, show only icon with tooltip
  if (!isSidebarExpanded && !isMobile) {
    if (nav.hasChildren && nav.children?.length) {
      return (
        <div key={index} className="flex justify-center w-full">
          <button
            onClick={() => {
              if (nav.locked) {
                handleClick();
              } else {
                toggleNavItem(nav.path);
              }
            }}
            className={`${baseClasses} px-3 py-2 justify-center ${hoverClasses} ${isActive ? activeClasses : textClasses} ${lockedClasses}`}
            title={nav.title}
          >
            {nav.icon && <nav.icon className="h-5 w-5" />}
          </button>
        </div>
      );
    }

    return (
      <div key={index} className="flex justify-center w-full">
        <Tooltip message={nav.title}>
          <NavLink
            to={`${getDashboardBase()}${nav.path}`}
            onClick={onMenuClick}
            className={`${baseClasses} px-3 py-2 justify-center ${hoverClasses} ${isActive ? activeClasses : textClasses} ${lockedClasses}`}
            title={nav.title}
          >
            {nav.icon && <nav.icon className="h-5 w-5" />}
          </NavLink>
        </Tooltip>
      </div>
    );
  }

  if (nav.hasChildren && nav.children?.length) {
    return (
      <div key={index} className="w-full space-y-0.5">
        <button
          onClick={handleClick}
          className={`${baseClasses} px-4 py-2.5 ${hoverClasses} ${isActive ? activeClasses : textClasses} ${lockedClasses}`}
        >
          {nav.icon && (
            <span className="mr-3">
              <nav.icon className="h-5 w-5" />
            </span>
          )}
          <span className="flex-1 text-left">{nav.title}</span>
          {nav.locked && <Lock className="h-4 w-4 ml-2" />}
          <ChevronDown className={`ml-2 h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`} />
        </button>

        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={isExpanded ? { opacity: 1, y: 0 } : { opacity: 0, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className={`space-y-0.5 border-l-2 border-gray-100 dark:border-darkBg-border overflow-hidden transition-all duration-300 ${isMobile ? 'pl-[2.3rem]' : 'ml-5 pl-2'}`}>
              {nav.children.map((child: ISideBarSubItem, childIndex: number) => (
                <NavLink
                  key={childIndex}
                  to={`${getDashboardBase()}${child.path}`}
                  onClick={onMenuClick}
                  className={`${baseClasses} px-3 py-2 ${hoverClasses} ${(isActiveSubNav(child.path) || activeSubNav(Number(child.id))) ? 'bg-brolly-yellow/10 font-medium' : textClasses}`}
                >
                  {child.icon && (
                    <span className="mr-3">
                      <child.icon className="h-5 w-5" />
                    </span>
                  )}
                  <span className="truncate">{child.title}</span>
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    );
  }

  return (
    <>
      {nav.locked ? (
        <button
          key={index}
          onClick={handleClick}
          className={`${baseClasses} px-4 py-2.5 ${hoverClasses} ${isActive ? activeClasses : textClasses} ${lockedClasses}`}
        >
          {nav.icon && (
            <span className="mr-3">
              <nav.icon className="h-5 w-5" />
            </span>
          )}
          <span className="flex-1 text-left">{nav.title}</span>
          {nav.locked && <Lock className="h-4 w-4 ml-2" />}
        </button>
      ) : (
        <NavLink
          key={index}
          to={`${getDashboardBase()}${nav.path}`}
          onClick={onMenuClick}
          className={`${baseClasses} px-4 py-2.5 ${hoverClasses} ${isActive ? activeClasses : textClasses}`}
        >
          {nav.icon && (
            <span className="mr-3">
              <nav.icon className="h-5 w-5" />
            </span>
          )}
          <span className="flex-1 text-left">{nav.title}</span>
        </NavLink>
      )}
      <ModalComponent/>
    </>
  );
}
